import axios from 'axios';
import { authHeader } from './auth';

const BASE = process.env.REACT_APP_NHIS_API_URL
  ? String(process.env.REACT_APP_NHIS_API_URL).replace(/\/$/, '')
  : String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/**
 * GET /api/nhis/dashboard
 * 수집된 NHIS raw 데이터를 대시보드용으로 가공해서 제공
 */
export async function nhisDashboardGet() {
  const res = await axios.get(`${BASE}/api/nhis/dashboard`, {
    headers: authHeader(),
  });
  if (!res.data || res.data.success === false) {
    const err = new Error(res.data?.message || '대시보드 데이터 조회 실패');
    throw err;
  }
  return res.data.data ?? res.data;
}

// 개발/테스트용 mock 데이터
export const MOCK_DASHBOARD = {
  collectedAt: '2025-12-31',
  patientGender: 'male',
  latestCheckup: {
    date: '20251231',
    facility: '마포리더스내과의원',
    result: '질환의심',
    note: '',
  },
  checkupHistory: [
    {
      date: '20251231',
      facility: '마포리더스내과의원',
      result: '질환의심',
      note: '',
    },
    {
      date: '20231019',
      facility: '마포리더스내과의원',
      result: '질환의심',
      note: '',
    },
    {
      date: '20211213',
      facility: '망원건강의원',
      result: '질환의심',
      note: '',
    },
  ],
  keyMetrics: [
    {
      group: '계측검사',
      name: '혈압(최고/최저)',
      value: '137/87',
      unit: 'mmHg',
      status: 'caution',
      deltaLabel: '주의 구간',
      deltaType: 'mid',
    },
    {
      group: '혈액검사',
      name: '공복혈당',
      value: '92',
      unit: 'mg/dL',
      status: 'normal',
      deltaLabel: '안정 범위',
      deltaType: 'down',
    },
    {
      group: '계측검사',
      name: 'BMI',
      value: '32.0',
      unit: 'kg/m2',
      status: 'danger',
      deltaLabel: '위험 구간',
      deltaType: 'up',
    },
    {
      group: '혈액검사',
      name: '신사구체여과율(GFR)',
      value: '87',
      unit: 'mL/min/1.73m2',
      status: 'normal',
      deltaLabel: '안정 범위',
      deltaType: 'down',
    },
  ],
  trendData: [
    {
      group: '혈액검사',
      indicator: '공복혈당',
      unit: 'mg/dL',
      values: [
        { date: '20211213', value: 79 },
        { date: '20231019', value: 84 },
        { date: '20251205', value: 92 },
      ],
    },
    {
      group: '계측검사',
      indicator: '수축기 혈압',
      unit: 'mmHg',
      values: [
        { date: '20211213', value: 132 },
        { date: '20231019', value: 124 },
        { date: '20251205', value: 137 },
      ],
    },
    {
      group: '계측검사',
      indicator: '이완기 혈압',
      unit: 'mmHg',
      values: [
        { date: '20211213', value: 87 },
        { date: '20231019', value: 82 },
        { date: '20251205', value: 87 },
      ],
    },
    {
      group: '계측검사',
      indicator: 'BMI',
      unit: 'kg/m2',
      values: [
        { date: '20211213', value: 26.8 },
        { date: '20231019', value: 28.2 },
        { date: '20251205', value: 32.0 },
      ],
    },
    {
      group: '혈액검사',
      indicator: '신사구체여과율(GFR)',
      unit: 'mL/min/1.73m2',
      values: [
        { date: '20211213', value: 79 },
        { date: '20231019', value: 89 },
        { date: '20251205', value: 87 },
      ],
    },
  ],
  healthCheckupOverview: {
    commonItems: [
      {
        group: '계측검사',
        targetDisease: '비만',
        item: '신장',
        unit: 'Cm',
        status: { code: 'unknown', label: '확인필요' },
        latest: { date: '20251205', value: '168.6', numericValue: 168.6 },
        availability: { code: 'applicable', label: '해당' },
        history: [
          { date: '20211213', value: '169.0', numericValue: 169.0 },
          { date: '20231019', value: '168.2', numericValue: 168.2 },
          { date: '20251205', value: '168.6', numericValue: 168.6 },
        ],
      },
      {
        group: '계측검사',
        targetDisease: '비만',
        item: '체중',
        unit: 'Kg',
        status: { code: 'unknown', label: '확인필요' },
        latest: { date: '20251205', value: '91.0', numericValue: 91.0 },
        availability: { code: 'applicable', label: '해당' },
        history: [
          { date: '20211213', value: '76.5', numericValue: 76.5 },
          { date: '20231019', value: '79.8', numericValue: 79.8 },
          { date: '20251205', value: '91.0', numericValue: 91.0 },
        ],
      },
      {
        group: '계측검사',
        targetDisease: '비만',
        item: '허리둘레',
        unit: 'Cm',
        status: { code: 'suspected', label: '복부비만' },
        latest: { date: '20251205', value: '103.0', numericValue: 103.0 },
        availability: { code: 'applicable', label: '해당' },
        history: [
          { date: '20211213', value: '79.0', numericValue: 79.0 },
          { date: '20231019', value: '90.0', numericValue: 90.0 },
          { date: '20251205', value: '103.0', numericValue: 103.0 },
        ],
      },
      {
        group: '계측검사',
        targetDisease: '고혈압',
        item: '혈압(최고/최저)',
        unit: 'mmHg',
        status: { code: 'borderline', label: '고혈압 전단계' },
        latest: { date: '20251205', value: '137/87', numericValue: 137 },
        availability: { code: 'applicable', label: '해당' },
      },
      {
        group: '혈액검사',
        targetDisease: '당뇨병',
        item: '공복혈당',
        unit: 'mg/dL',
        status: { code: 'normal', label: '정상' },
        latest: { date: '20251205', value: '92', numericValue: 92 },
        availability: { code: 'applicable', label: '해당' },
      },
      {
        group: '계측검사',
        targetDisease: '비만',
        item: '체질량지수',
        unit: 'kg/m2',
        status: { code: 'suspected', label: '비만' },
        latest: { date: '20251205', value: '32.0', numericValue: 32.0 },
        availability: { code: 'applicable', label: '해당' },
        history: [
          { date: '20211213', value: '26.8', numericValue: 26.8 },
          { date: '20231019', value: '28.2', numericValue: 28.2 },
          { date: '20251205', value: '32.0', numericValue: 32.0 },
        ],
      },
      {
        group: '요검사',
        targetDisease: '신장질환',
        item: '요단백',
        unit: '',
        status: { code: 'borderline', label: '경계' },
        latest: { date: '20251205', value: '약양성', numericValue: null },
        availability: { code: 'applicable', label: '해당' },
      },
      {
        group: '혈액검사',
        targetDisease: '만성신장질환',
        item: '신사구체여과율(GFR)',
        unit: 'mL/min/1.73m2',
        status: { code: 'normal', label: '정상' },
        latest: { date: '20251205', value: '87', numericValue: 87 },
        availability: { code: 'applicable', label: '해당' },
      },
    ],
    optionalItems: [
      {
        group: '혈액검사',
        targetDisease: '이상지질혈증',
        item: '총콜레스테롤',
        unit: 'mg/dL',
        status: { code: 'normal', label: '정상' },
        latest: { date: '20231019', value: '183', numericValue: 183 },
        availability: { code: 'applicable', label: '해당' },
      },
      {
        group: '폐기능검사',
        targetDisease: '만성폐쇄성폐질환',
        item: '만성폐쇄성폐질환',
        status: { code: 'not_applicable', label: '비해당' },
        latest: null,
        availability: {
          code: 'not_applicable',
          label: '비해당',
          reason: '56·66세 대상 폐기능검사 항목입니다.',
        },
      },
      {
        group: '골다공증',
        targetDisease: '골다공증',
        item: '골다공증',
        status: { code: 'not_applicable', label: '비해당' },
        latest: null,
        availability: {
          code: 'not_applicable',
          label: '비해당',
          reason: '54·60·66세 여성 대상 항목입니다.',
        },
      },
    ],
  },
  cancerScreenings: [
    {
      date: '20260102',
      type: '암(대장)',
      facility: '마포리더스내과의원',
      result: '',
      note: '',
    },
    {
      date: '20241105',
      type: '암(위)',
      facility: '마포리더스내과의원',
      result: '정상',
      note: '',
    },
    {
      date: '20221018',
      type: '암(대장)',
      facility: '망원건강의원',
      result: '정상',
      note: '',
    },
  ],
  medicalUsage: {
    totalVisits: 49,
    facilityCount: 14,
    latestDate: '20251231',
    breakdown: {
      '일반외래': 12,
      '처방조제': 18,
      '치과외래': 5,
    },
    recentRecords: [
      {
        facilityName: '마포리더스내과의원',
        date: '20251231',
        type: '일반외래',
        prescriptionCount: 2,
        latest: true,
        tags: ['검진연계'],
      },
      {
        facilityName: '망원올리브약국',
        date: '20251231',
        type: '처방조제',
        prescriptionCount: 3,
        latest: true,
        tags: [],
      },
      {
        facilityName: '연세스마일치과',
        date: '20251105',
        type: '치과외래',
        prescriptionCount: 0,
        latest: false,
        tags: [],
      },
      {
        facilityName: '마포리더스내과의원',
        date: '20250920',
        type: '일반외래',
        prescriptionCount: 1,
        latest: false,
        tags: [],
      },
      {
        facilityName: '서울아산병원',
        date: '20250715',
        type: '일반외래',
        prescriptionCount: 2,
        latest: false,
        tags: [],
      },
    ],
  },
  analysisProgress: {
    percent: 64,
    steps: [
      { label: '데이터 정규화', status: 'done' },
      { label: '위험도 계산', status: 'run' },
      { label: '리포트 생성', status: 'wait' },
    ],
  },
  medication: {
    totalPrescriptions: 56,
    uniqueDrugs: 22,
    latestDate: '20251231',
    categoryDistribution: [
      { category: '해열·진통·소염', count: 18 },
      { category: '소화성궤양용제', count: 12 },
      { category: '혈압강하제', count: 9 },
      { category: '당뇨병용제', count: 7 },
      { category: '고지혈증치료제', count: 5 },
    ],
    efficacyDistribution: [
      { category: '해열·진통·소염', count: 18 },
      { category: '소화성궤양용제', count: 12 },
      { category: '혈압강하제', count: 9 },
      { category: '당뇨병용제', count: 7 },
      { category: '고지혈증치료제', count: 5 },
    ],
    dispenseTypeDistribution: [
      { category: '약국', count: 44 },
      { category: '입원', count: 8 },
      { category: '외래', count: 4 },
    ],
    topRepeated: [
      { name: '이텍스니자티딘캡슐150mg', category: '소화성궤양용제', count: 6, lastDate: '20251231' },
      { name: '텔미원정50mg', category: '혈압강하제', count: 5, lastDate: '20251231' },
      { name: '아세클로페낙정100mg', category: '해열·진통·소염', count: 4, lastDate: '20251118' },
    ],
    recentBundle: {
      date: '20251231',
      facility: '망원올리브약국',
      items: [
        { name: '이텍스니자티딘캡슐150mg', category: '소화성궤양용제', days: 14 },
        { name: '텔미원정50mg', category: '혈압강하제', days: 30 },
        { name: '아세클로페낙정100mg', category: '해열·진통·소염', days: 5 },
      ],
    },
  },
};
